import * as React from 'react';
import DropDownListField from '../../../commonComponents/fields/DropDownListField';
import Field from '../../../commonComponents/fields/Field';
import MultipleChoiceField from '../../../commonComponents/fields/MultipleChoiceField';
import RangeField from '../../../commonComponents/fields/RangeField';
import RefereeQuestion, { RefereeQuestionTypes } from '../../../models/be/RefereeQuestion';
import TextAreaField from '../../../commonComponents/fields/TextAreaField';
import DateField from '../../../commonComponents/fields/DateField';
import { RefereeAnswer } from '../../../models/be/RefereeAnswer';
import RadioQuestion from '../../common/fields/RadioQuestion';
import { metallicBlue } from '../../cssConstants';

const RefereeQuestionField = (props: Props) => {
	const { name, onAnswerChange, refereeAnswer, refereeQuestion, questionNumber } = props;
	const { mandatory, text } = refereeQuestion;
	const label = `${questionNumber}. ${text}`;

	if (props.refereeQuestion.type === RefereeQuestionTypes.Text) {
		return (
			<TextAreaField
				label={label}
				// TODO: Name should be simplified to house just the index, and name of value. eg. answerText[props.i]
				name={name}
				id={questionNumber}
				value={refereeAnswer as string}
				onValueChange={onAnswerChange}
				required={mandatory}
			/>
		);
	}

	const onNumberChange = (fieldName: string, value: string, errorMessage: string) => {
		let number: number = null;
		if (typeof value === 'number') {
			number = value as number;
		} else {
			// This will parse integers correctly. It will not make "2" a float of 2.0, but instead 2.
			number = parseFloat(value);
		}
		props.onAnswerChange(fieldName, number, errorMessage);
	};

	if (props.refereeQuestion.type === RefereeQuestionTypes.Number) {
		return (
			<Field
				type="number"
				min={refereeQuestion.minValue}
				max={refereeQuestion.maxValue}
				step={getDecimalPlacesStepString(refereeQuestion.decimalPlaces)}
				label={label}
				name={name}
				id={questionNumber}
				value={refereeAnswer as number}
				onValueChange={onNumberChange}
				required={mandatory}
				validateOnMount={false}
			/>
		);
	}

	if (props.refereeQuestion.type === RefereeQuestionTypes.Dropdown)
		return (
			<DropDownListField
				label={label}
				name={name}
				fieldId={questionNumber}
				data={refereeQuestion.options}
				value={refereeAnswer as string}
				onValueChange={onAnswerChange}
				required={mandatory}
				validateOnMount={false}
				width="50%"
			/>
		);

	if (props.refereeQuestion.type === RefereeQuestionTypes.Boolean)
		return (
			<RadioQuestion
				label={label}
				options={[
					{ name: 'Yes', value: true },
					{ name: 'No', value: false }
				]}
				value={refereeAnswer as boolean}
				name={name}
				fieldId={questionNumber}
				onValueChange={props.onAnswerChange}
				labelColour={metallicBlue}
			/>
		);

	if (props.refereeQuestion.type === RefereeQuestionTypes.Date)
		return (
			<DateField
				label={label}
				name={name}
				fieldId={questionNumber}
				onValueChange={props.onAnswerChange}
				value={refereeAnswer && new Date(refereeAnswer as string)}
				required={mandatory}
				max={null}
				width="50%"
			/>
		);

	if (props.refereeQuestion.type === RefereeQuestionTypes.CheckList)
		return (
			<MultipleChoiceField
				type="checkbox"
				label={label}
				onValueChange={props.onAnswerChange}
				name={name}
				fieldId={questionNumber}
				options={props.refereeQuestion.options}
				value={refereeAnswer as string[]}
				required={mandatory}
			/>
		);

	if (props.refereeQuestion.type === RefereeQuestionTypes.RadioList) {
		return (
			<MultipleChoiceField
				type="radio"
				label={label}
				onValueChange={props.onAnswerChange}
				name={name}
				fieldId={questionNumber}
				options={props.refereeQuestion.options}
				value={refereeAnswer as string[]}
				required={mandatory}
			/>
		);
	}

	// This might not even display on phones, needs work to be supported but isn't required spec yet.
	if (props.refereeQuestion.type === RefereeQuestionTypes.Slider)
		return (
			<RangeField
				label={label}
				min={props.refereeQuestion.minValue}
				max={props.refereeQuestion.maxValue}
				value={refereeAnswer as number}
				name={name}
				id={questionNumber}
				onValueChange={onNumberChange}
				required={mandatory}
			/>
		);

	throw new Error(
		`This question does not have a valid question type. Its type is listed as "${props.refereeQuestion.type}"`
	);
};

interface Props {
	questionNumber: string;
	name: string;
	refereeQuestion: RefereeQuestion;
	refereeAnswer: RefereeAnswer;
	onAnswerChange: (name: string, value: RefereeAnswer, errorMessage: string) => void;
}

const getDecimalPlacesStepString = (decimalPlaces: number) => {
	if (!decimalPlaces) return 1;
	const numberOfZeros = decimalPlaces - 1;
	let allowedDecimals = '0.1';
	for (let i = 0; i < numberOfZeros; i++) {
		allowedDecimals = `${allowedDecimals.substr(0, 2)}0${allowedDecimals.substr(2)}`;
	}
	return allowedDecimals;
	// TODO Consider simplifying down to return (1 / Math.power(10, decimalPlaces)).toString();
};

export default RefereeQuestionField;
